import React, { useContext, useState } from "react";
import { TableHead } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExpandMore } from "@mui/icons-material";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PartsInformation from "./PartsInformation";
import language from '../../../nls/language.json';
import Context from "../../../context/Context";
import './styles/AdditionalInformation.css'

function AdditionalInformation({
  data,
  sagA_Pecas,
  sagA_Tool,
  sagA_GlassLot,
  sagA_MoldesGalgas,
  sagA_Components,
  sagA_RastreabilidadeSaga,
  loading = {}
}) {
  const [expanded, setExpanded] = useState(false);
  const {
    selectedCountry,
  } = useContext(Context);

  var style = {
    padding: 0.6,
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  
  const asList = (value) => (Array.isArray(value) ? value : []);
  const materiaisConsumidos = asList(sagA_Pecas);
  const tools = asList(sagA_Tool);
  const glassLot = asList(sagA_GlassLot);
  const moldesGalgas = asList(sagA_MoldesGalgas);
  const components = asList(sagA_Components);
  const rastreabilidadeSaga = asList(sagA_RastreabilidadeSaga);

  const pecasLoading = !!loading.pecas;
  const toolsLoading = !!loading.tools;
  const vidrosLoading = !!(loading.glassLot || loading.componentes);
  const moldesLoading = !!loading.moldes;
  const rastreabilidadeLoading = !!loading.rastreabilidade;

  // "2026-09-30T14:22:05" → "30/09/2026 14:22:05" (sem conversão de fuso).
  const formatDateTime = (value) =>
    value ? value.replace(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2}:\d{2}).*$/, "$3/$2/$1 $4") : '';

  const renderLoadingIndicator = () => (
    <span className="accordionLoadingIndicator">
      <FontAwesomeIcon icon={faSpinner} spin />
      <span className="accordionLoadingText">
        {language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.carregando}
      </span>
    </span>
  );

  // Título do accordion com spinner enquanto o request da seção não termina.
  const renderTitle = (text, isLoading) => (
    <p className="accordionTitle">
      {text}
      {isLoading && renderLoadingIndicator()}
    </p>
  );

  const renderLoadingBody = () => (
    <div className="accordionLoading">
      {renderLoadingIndicator()}
    </div>
  );

  // Tabela de "Materiais consumidos no SAP". A ordem das linhas vem do backend.
  const renderMateriaisTable = (rows) => (
    <TableContainer
      component={Paper}
      sx={{ maxHeight: 600, overflow: "auto" }}
    >
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.posicao}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.codMaterial}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.material}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.deposito}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.lote}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.grupoMat}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.Documento}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.Movimento}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.qtdeConsumo}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.usuario}</TableCell>
            <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.tableHeader.dataHora}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((pecas, index) => (
            <TableRow
              key={index}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell sx={style}>{pecas.Ordem_Material || ''}</TableCell>
              <TableCell sx={style}>{pecas.COD_MATERIAL || ''}</TableCell>
              <TableCell sx={style}>{pecas.MATERIAL || ''}</TableCell>
              <TableCell sx={style}>{pecas.DEPOSITO || ''}</TableCell>
              <TableCell sx={style}>{pecas.LOTE || ''}</TableCell>
              <TableCell sx={style}>{pecas.GRUPO_MAT || ''}</TableCell>
              <TableCell sx={style}>{pecas.DOCUMENTO || ''}</TableCell>
              <TableCell sx={style}>{pecas.MOVIMENTO || ''}</TableCell>
              <TableCell sx={style}>{pecas.QTDE_CONSUMO || ''}</TableCell>
              <TableCell sx={style}>{pecas.USUARIO || ''}</TableCell>
              <TableCell sx={style}>
                {pecas.DT_LANCAMENTO_T && pecas.HR_LANCAMENTO_T
                  ? `${pecas.DT_LANCAMENTO_T} ${pecas.HR_LANCAMENTO_T}`
                  : ''}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="header-flex accordionSummary"
        >
          <p>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.informacoesDaPeca.title}</p>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, py: 1.5 }}>
          <PartsInformation data={data} />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="header-flex accordionSummary"
        >
          {renderTitle(language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.materiaisConsumidos.title, pecasLoading)}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {pecasLoading ? renderLoadingBody() : materiaisConsumidos.length === 0 ? (
            <h2>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.semInformacoes}</h2>
          ) : (
            renderMateriaisTable(materiaisConsumidos)
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel5"}
        onChange={handleChange("panel5")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="header-flex accordionSummary"
        >
          {renderTitle(language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.title, rastreabilidadeLoading)}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {rastreabilidadeLoading ? renderLoadingBody() : rastreabilidadeSaga.length === 0 ? (
            <h2>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.semInformacoes}</h2>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 600, overflow: "auto" }}
            >
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.tipo}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.codMaterial}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.classe}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.loteCorte}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.posicao}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.loteConsumo}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.usuario}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.loteFornecedor}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.rastreabilidadeSaga.tableHeader.dataHora}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Ordem definida pelo backend (tipo → data/hora → posição) */}
                  {rastreabilidadeSaga.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell sx={style}>{item.tipo || ''}</TableCell>
                      <TableCell sx={style}>{item.mat_Codigo || ''}</TableCell>
                      <TableCell sx={style}>{item.mat_Classe || ''}</TableCell>
                      <TableCell sx={style}>{item.lote_Corte || ''}</TableCell>
                      <TableCell sx={style}>{item.ordem_Posicao || ''}</TableCell>
                      <TableCell sx={style}>{item.lote_Consumo || ''}</TableCell>
                      <TableCell sx={style}>{item.apontamento_Usuario || ''}</TableCell>
                      <TableCell sx={style}>{item.lote_Fornecedor || ''}</TableCell>
                      <TableCell sx={style}>{formatDateTime(item.apontamento_Hora)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel2"}
        onChange={handleChange("panel2")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="header-flex accordionSummary"
        >
          {renderTitle(language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.serigrafia.title, toolsLoading)}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {toolsLoading ? renderLoadingBody() : tools.length === 0 ? (
            <h2>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.semInformacoes}</h2>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 600, overflow: "auto" }}
            >
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.serigrafia.tableHeader.codigo}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.serigrafia.tableHeader.descricao}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.serigrafia.tableHeader.localSaga}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.serigrafia.tableHeader.status}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tools.map((tool, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell sx={style}>{tool.ferramental_Codigo}</TableCell>
                        <TableCell sx={style}>{tool.ferramental_Descricao}</TableCell>
                        <TableCell sx={style}>{tool.ferramental_LocalSAGA}</TableCell>
                        <TableCell sx={style}>{tool.ferramental_Status}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel3"}
        onChange={handleChange("panel3")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="header-flex accordionSummary"
        >
          {renderTitle(language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.title, vidrosLoading)}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {vidrosLoading ? renderLoadingBody() : glassLot.length === 0 ? (
            <h2>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.semInformacoes}</h2>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 600, overflow: "auto" }}
            >
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.tableHeader.codMaterial}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.tableHeader.material}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.tableHeader.lote}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.tableHeader.dataHora}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.tableHeader.usuario}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.tableHeader.qtdeConsumo}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.vidros.tableHeader.sets}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {glassLot.map((vidro, index) => (
                    <TableRow
                      key={index}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell sx={style}>{vidro.CD_CONTA_CONTABIL}</TableCell>
                      <TableCell sx={style}>{vidro.MATERIAL}</TableCell>
                      <TableCell sx={style}>{vidro.LOTE}</TableCell>
                      <TableCell sx={style}>{vidro.DT_HORA}</TableCell>
                      <TableCell sx={style}>{vidro.USUARIO}</TableCell>
                      <TableCell sx={style}>{vidro.QTDE_CONSUMO}</TableCell>
                      <TableCell sx={style}>{vidro.SETS}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!vidrosLoading && components.length > 0 && (
            <>
              <p style={{ padding: "8px 16px", fontWeight: "bold", margin: 0 }}>
                Componentes de Corte
              </p>
              <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: "auto" }}>
                <Table stickyHeader aria-label="componentes table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={style}>Pos</TableCell>
                      <TableCell sx={style}>Cód. Material</TableCell>
                      <TableCell sx={style}>Nome</TableCell>
                      <TableCell sx={style}>ClaveModelo</TableCell>
                      <TableCell sx={style}>m²</TableCell>
                      <TableCell sx={style}>Un. Med</TableCell>
                      <TableCell sx={style}>Descrição Eng.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {components.map((comp, index) => (
                      <TableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell sx={style}>{comp.pos}</TableCell>
                        <TableCell sx={style}>{comp.desenho_CodMat}</TableCell>
                        <TableCell sx={style}>{comp.desenho_Descricao}</TableCell>
                        <TableCell sx={style}>{comp.desenho_ClaveModelo}</TableCell>
                        <TableCell sx={style}>{comp.m2}</TableCell>
                        <TableCell sx={style}>{comp.unMed}</TableCell>
                        <TableCell sx={style}>{comp.descricao_Eng}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel4"}
        onChange={handleChange("panel4")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="header-flex accordionSummary"
        >
          {renderTitle(language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.title, moldesLoading)}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {moldesLoading ? renderLoadingBody() : moldesGalgas.length === 0 ? (
            <h2>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.semInformacoes}</h2>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: "auto" }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.tableHeader.ordemSerial}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.tableHeader.tipo}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.tableHeader.posicao}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.tableHeader.codigo}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.tableHeader.grupo}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.tableHeader.descricao}</TableCell>
                    <TableCell sx={style}>{language[selectedCountry.name].Dashboard.pecasSetor.ficha.info.moldesGalgas.tableHeader.ferramental}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {moldesGalgas.map((vidro, index) => (
                    <TableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell sx={style}>{vidro.ordem_Serial}</TableCell>
                      <TableCell sx={style}>{vidro.tipo}</TableCell>
                      <TableCell sx={style}>{vidro.posicao_Lista}</TableCell>
                      <TableCell sx={style}>{vidro.ferr_Codigo}</TableCell>
                      <TableCell sx={style}>{vidro.ferr_Grupo}</TableCell>
                      <TableCell sx={style}>{vidro.ferr_Descricao}</TableCell>
                      <TableCell sx={style}>{vidro.local_Ferramental}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
}

export default AdditionalInformation;